import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";

import {
  fetchPandoChargers,
  fetchPandoChargerHistory,
  type PandoHealthRow,
  type PandoStatus,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DataTable } from "@/components/ui/data-table";
import { DataTableShell } from "@/components/ui/data-table-shell";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Refresh } from "@/components/icons";


function StatusBadge({ status }: { status: PandoStatus }) {
  const variant =
    status === "ok" ? "success"
    : status === "unreachable" || status === "timeout" ? "warning"
    : "destructive";
  return <Badge variant={variant as any}>{status}</Badge>;
}

function formatCheckDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function tmpDisplay(row: PandoHealthRow): string {
  if (row.tmp_used_mb == null || row.tmp_total_mb == null) return "—";
  return `${row.tmp_used_mb} / ${row.tmp_total_mb} MB`;
}

function tmpPercent(row: PandoHealthRow): number | null {
  if (row.tmp_used_mb == null || row.tmp_total_mb == null || row.tmp_total_mb === 0) return null;
  return Math.round((row.tmp_used_mb / row.tmp_total_mb) * 100);
}

function uptimeDisplay(sec: number | null): string {
  if (sec == null) return "—";
  if (sec < 3600) return `${Math.round(sec / 60)}m`;
  if (sec < 86400) return `${Math.round(sec / 3600)}h`;
  return `${Math.round(sec / 86400)}d`;
}

const columns: ColumnDef<PandoHealthRow>[] = [
  {
    accessorKey: "serial",
    header: "Serial",
    cell: ({ row }) => <span className="font-mono text-xs">{row.original.serial}</span>,
  },
  {
    id: "last_check",
    header: "Last check",
    cell: ({ row }) => {
      const ok = row.original.status === "ok";
      const checkTs = formatCheckDate(row.original.ts);
      const lastOk = row.original.last_ok_ts ? formatCheckDate(row.original.last_ok_ts) : null;
      return (
        <div className="text-xs font-mono leading-tight">
          <div className={ok ? "text-success" : "text-destructive"} title={row.original.ts}>
            {ok ? "✓" : "✗"} {checkTs}
          </div>
          {!ok && (
            <div className="opacity-60">last ok: {lastOk ?? "never"}</div>
          )}
        </div>
      );
    },
  },
  {
    id: "tmp",
    header: "/tmp",
    cell: ({ row }) => {
      const pct = tmpPercent(row.original);
      const danger = pct !== null && pct >= 80;
      return (
        <span className={["text-xs", danger ? "text-destructive font-semibold" : ""].join(" ")}>
          {tmpDisplay(row.original)}
          {pct !== null && <span className="opacity-60"> ({pct}%)</span>}
        </span>
      );
    },
  },
  {
    id: "logs",
    header: "Logs dir",
    cell: ({ row }) => {
      const v = row.original.logs_dir_mb;
      if (v == null) return <span className="text-xs opacity-60">—</span>;
      const danger = v >= 100;
      return (
        <span className={["text-xs", danger ? "text-destructive font-semibold" : ""].join(" ")}>
          {v} MB
        </span>
      );
    },
  },
  {
    id: "uptime",
    header: "Uptime",
    cell: ({ row }) => <span className="text-xs">{uptimeDisplay(row.original.uptime_sec)}</span>,
  },
  {
    accessorKey: "action",
    header: "Last action",
    cell: ({ row }) => <span className="text-xs">{row.original.action}</span>,
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => <StatusBadge status={row.original.status} />,
  },
];


export function PandoChargersPage() {
  const [openSerial, setOpenSerial] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["pandoChargers"],
    queryFn: fetchPandoChargers,
    refetchInterval: 30_000,
  });

  const rows = q.data?.chargers ?? [];

  return (
    <div className="space-y-5 pt-[86px]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Pando Chargers</h1>
          <div className="text-sm opacity-70">EverEST log rotation health (admin)</div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => q.refetch()}>
            <Refresh className="h-4 w-4" /> Refresh
          </Button>
        </div>
      </div>

      {q.isError ? (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-destructive">
          Failed to load Pando charger health
        </div>
      ) : (
        <DataTableShell>
          <DataTable
            columns={columns}
            data={rows}
            isLoading={q.isLoading}
            emptyMessage="No Pando charger records yet — run `python -m scripts.pando_ssh_ops probe --all`"
            onRowClick={(row) => setOpenSerial(row.serial)}
          />
        </DataTableShell>
      )}

      <div className="text-center text-xs opacity-50">
        {rows.length} charger(s) • auto-refresh 30s
      </div>

      <HistoryDialog serial={openSerial} onClose={() => setOpenSerial(null)} />
    </div>
  );
}


function HistoryDialog({ serial, onClose }: { serial: string | null; onClose: () => void }) {
  const q = useQuery({
    queryKey: ["pandoChargerHistory", serial],
    queryFn: () => fetchPandoChargerHistory(serial!, 50),
    enabled: !!serial,
  });

  return (
    <Dialog open={!!serial} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-mono">{serial}</DialogTitle>
        </DialogHeader>
        {q.isLoading ? (
          <div className="text-sm opacity-60">Loading history…</div>
        ) : q.isError ? (
          <div className="text-sm text-destructive">Failed to load history</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-left opacity-70">
              <tr>
                <th className="py-1 pr-3">When</th>
                <th className="py-1 pr-3">Action</th>
                <th className="py-1 pr-3">Status</th>
                <th className="py-1 pr-3">/tmp</th>
                <th className="py-1 pr-3">Logs</th>
                <th className="py-1 pr-3">Rotate</th>
                <th className="py-1 pr-3">Error</th>
              </tr>
            </thead>
            <tbody>
              {(q.data?.history ?? []).map((r, i) => (
                <tr key={i} className="border-t border-border/40">
                  <td className="py-1 pr-3 font-mono whitespace-nowrap">{r.ts}</td>
                  <td className="py-1 pr-3">{r.action}</td>
                  <td className="py-1 pr-3"><StatusBadge status={r.status} /></td>
                  <td className="py-1 pr-3">{tmpDisplay(r)}</td>
                  <td className="py-1 pr-3">{r.logs_dir_mb == null ? "—" : `${r.logs_dir_mb} MB`}</td>
                  <td className="py-1 pr-3">
                    {r.before_mb != null && r.after_mb != null
                      ? `${r.before_mb}→${r.after_mb} MB`
                      : "—"}
                  </td>
                  <td className="py-1 pr-3 text-destructive">{r.error ?? ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </DialogContent>
    </Dialog>
  );
}
